import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { logActivity } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Search, Users, MoreHorizontal, UserCheck, UserX, Archive, ArchiveRestore, Download } from "lucide-react";
import { StatusBadge, PageHeader, EmptyState, formatDate } from "@/lib/ui";
import { downloadCsv, iso } from "@/lib/csvExport";

const EMPTY = {
  first_name: "", last_name: "", preferred_name: "",
  date_of_birth: "", phone: "", email: "",
  street_address: "", city: "", state: "", zip_code: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  insurance_info: "", referral_source: "",
  intake_date: "", status: "active", notes: "",
};

// Filter tabs shown at the top of the list.
// "Archived" is intentionally separate — status ("active"/"pending"/"inactive")
// and archived (boolean) are two independent concepts in this schema.
const TABS = ["all", "active", "pending", "inactive", "archived"];

export default function Clients() {
  const { currentOrgId, user, currentRole } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const isAdmin = currentRole === "org_admin";

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (statusFilter && statusFilter !== "all") next.set("status", statusFilter); else next.delete("status");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, statusFilter]);

  // Load ALL clients (including archived) — filtering happens client-side so
  // counters remain accurate across every bucket without a second round trip.
  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    const { data, error } = await supabase.from("clients").select("*")
      .eq("organization_id", currentOrgId).order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [currentOrgId]);
  useEffect(() => {
    if (params.get("new") === "1") { setOpen(true); params.delete("new"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim())
      return toast.error("First name and last name are required");
    setSaving(true);
    const payload = { ...form };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    payload.organization_id = currentOrgId;
    payload.created_by = user.id;
    payload.first_name = form.first_name.trim();
    payload.last_name = form.last_name.trim();
    payload.status = form.status;
    const { data, error } = await supabase.from("clients").insert(payload).select().single();
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Client added");
    setForm(EMPTY);
    setOpen(false);
    logActivity({
      organization_id: currentOrgId, actor_user_id: user.id, client_id: data.id,
      action: "client.created", summary: `added ${data.first_name} ${data.last_name} as a client`,
      entity_type: "clients", entity_id: data.id,
    });
    navigate(`/app/clients/${data.id}`);
  };

  // ─── Row-level status actions ─────────────────────────────────────────────
  // These are intentionally NARROW: each one changes exactly one column so
  // "Inactive" and "Archived" cannot get conflated again. All keep every
  // relationship (tasks, documents, bookings, invoices, forms, contracts,
  // history) intact — this is a pure client-record flag change.
  //
  // .select() is CRITICAL — without it Supabase returns null on both success
  // AND silent-RLS-block. With it we get the affected rows back and can
  // detect a permission-denied silent block (data.length === 0, no error).
  const setClientStatus = async (client, nextStatus, verbSummary) => {
    const { data, error } = await supabase.from("clients")
      .update({ status: nextStatus }).eq("id", client.id).select();
    if (error) return toast.error(error.message);
    if (!data || data.length === 0) {
      return toast.error("Update was blocked. You may not have permission to change this client.");
    }
    toast.success(verbSummary.replace("{name}", `${client.first_name} ${client.last_name}`));
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: client.id,
      action: `client.status.${nextStatus}`, summary: verbSummary.replace("{name}", `${client.first_name} ${client.last_name}`),
      entity_type: "clients", entity_id: client.id });
    load();  // always refetch from server so no bucket can lie about the state
  };
  const setClientArchived = async (client, archived) => {
    const { data, error } = await supabase.from("clients")
      .update({ archived }).eq("id", client.id).select();
    if (error) return toast.error(error.message);
    if (!data || data.length === 0) {
      return toast.error("Update was blocked. You may not have permission to change this client.");
    }
    const verb = archived ? "archived" : "restored";
    toast.success(`Client ${verb}`);
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: client.id,
      action: `client.${verb}`, summary: `${verb} ${client.first_name} ${client.last_name}`,
      entity_type: "clients", entity_id: client.id });
    load();
  };

  const markInactive = (c) => setClientStatus(c, "inactive", "Marked {name} inactive");
  const reactivate  = (c) => setClientStatus(c, "active",   "Reactivated {name}");
  const archive     = (c) => {
    if (!window.confirm(`Archive ${c.first_name} ${c.last_name}? They'll be hidden from the normal client lists but all data is preserved.`)) return;
    setClientArchived(c, true);
  };
  const unarchive   = (c) => setClientArchived(c, false);

  // ─── Counters (computed from real data, all buckets) ──────────────────────
  const counters = useMemo(() => {
    const c = { total: rows.length, active: 0, pending: 0, inactive: 0, archived: 0 };
    for (const r of rows) {
      if (r.archived) { c.archived += 1; continue; }   // archived is its own bucket, not double-counted
      if (r.status === "active")   c.active   += 1;
      else if (r.status === "pending")  c.pending  += 1;
      else if (r.status === "inactive") c.inactive += 1;
    }
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((c) => {
      // Archived tab shows ONLY archived. Every other tab hides archived.
      if (statusFilter === "archived") { if (!c.archived) return false; }
      else if (c.archived) return false;
      if (statusFilter !== "all" && statusFilter !== "archived" && c.status !== statusFilter) return false;
      if (!q) return true;
      const s = [c.first_name, c.last_name, c.preferred_name, c.email, c.phone].filter(Boolean).join(" ").toLowerCase();
      return s.includes(q.toLowerCase());
    });
  }, [rows, q, statusFilter]);

  // Small reusable counter card — colour comes from the semantic status.
  const counterColour = (key) => ({
    total:    "bg-white",
    active:   "bg-emerald-50 border-emerald-100",
    pending:  "bg-amber-50 border-amber-100",
    inactive: "bg-stone-100 border-stone-200",
    archived: "bg-slate-100 border-slate-200",
  })[key];

  const CounterCard = ({ id, label, value, onClick, active }) => (
    <button
      data-testid={`counter-${id}`}
      onClick={onClick}
      className={`text-left rounded-2xl border px-4 py-3 min-w-[110px] transition-all hover:shadow-sm ${counterColour(id)} ${active ? "ring-2 ring-[#E8712C]/60" : ""}`}
    >
      <div className="text-xs uppercase tracking-widest text-[#57534E]">{label}</div>
      <div className="text-2xl font-semibold mt-1" data-testid={`counter-${id}-value`}>{value}</div>
    </button>
  );

  // Row-level dropdown of status/archive actions. Only shown to admins to
  // match the existing permission model (Archive was admin-only before).
  const RowActions = ({ c }) => {
    if (!isAdmin) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`client-actions-${c.id}`}
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#FAF8F5]"
          >
            <MoreHorizontal size={16} className="text-[#57534E]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {!c.archived && c.status !== "inactive" && (
            <DropdownMenuItem data-testid={`action-mark-inactive-${c.id}`} onClick={() => markInactive(c)}>
              <UserX size={14} className="mr-2" /> Mark inactive
            </DropdownMenuItem>
          )}
          {!c.archived && c.status === "inactive" && (
            <DropdownMenuItem data-testid={`action-reactivate-${c.id}`} onClick={() => reactivate(c)}>
              <UserCheck size={14} className="mr-2" /> Reactivate
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {!c.archived ? (
            <DropdownMenuItem data-testid={`action-archive-${c.id}`} onClick={() => archive(c)} className="text-red-600 focus:text-red-600">
              <Archive size={14} className="mr-2" /> Archive
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem data-testid={`action-restore-${c.id}`} onClick={() => unarchive(c)}>
              <ArchiveRestore size={14} className="mr-2" /> Restore
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="clients-page">
      <PageHeader
        title="Clients"
        subtitle="Manage every client in your workspace."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid="clients-export-csv"
              disabled={filtered.length === 0}
              onClick={() => downloadCsv(
                `clients-${iso(new Date())}`,
                [
                  { key: "first_name", label: "First name" },
                  { key: "last_name",  label: "Last name" },
                  { key: "preferred_name", label: "Preferred name" },
                  { key: "email",      label: "Email" },
                  { key: "phone",      label: "Phone" },
                  { key: "status",     label: "Status" },
                  { label: "Archived", get: (c) => c.archived ? "Yes" : "No" },
                  { key: "date_of_birth", label: "DOB", get: (c) => iso(c.date_of_birth) },
                  { key: "street_address", label: "Street" },
                  { key: "city",       label: "City" },
                  { key: "state",      label: "State" },
                  { key: "zip_code",   label: "ZIP" },
                  { key: "emergency_contact_name",  label: "Emergency contact" },
                  { key: "emergency_contact_phone", label: "Emergency phone" },
                  { key: "referral_source", label: "Referral source" },
                  { key: "intake_date", label: "Intake date", get: (c) => iso(c.intake_date) },
                  { label: "Created", get: (c) => iso(c.created_at) },
                ],
                filtered,
              )}
            ><Download size={14} className="mr-1.5" /> Export CSV</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={16} className="mr-1.5" /> Add new client</Button>
              </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl">
              <DialogHeader><DialogTitle className="font-display tracking-tight">New client</DialogTitle></DialogHeader>
              <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
                <div>
                  <h4 className="text-xs font-semibold text-[#57534E] uppercase tracking-widest mb-3">Personal information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>First name *</Label><Input data-testid="c-first-name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="mt-1.5" /></div>
                    <div><Label>Last name *</Label><Input data-testid="c-last-name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="mt-1.5" /></div>
                    <div><Label>Preferred name</Label><Input data-testid="c-pref-name" value={form.preferred_name} onChange={(e) => setForm({...form, preferred_name: e.target.value})} className="mt-1.5" /></div>
                    <div><Label>Date of birth</Label><Input data-testid="c-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} className="mt-1.5" /></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#57534E] uppercase tracking-widest mb-3">Contact information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Phone</Label><Input data-testid="c-phone" type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1.5" placeholder="(555) 123-4567" /></div>
                    <div><Label>Email</Label><Input data-testid="c-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1.5" /></div>
                  </div>
                  <div className="mt-3"><Label>Street address</Label><Input data-testid="c-street" value={form.street_address} onChange={(e) => setForm({...form, street_address: e.target.value})} className="mt-1.5" /></div>
                  <div className="mt-3 grid grid-cols-[1fr_120px_120px] gap-3">
                    <div><Label>City</Label><Input data-testid="c-city" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="mt-1.5" /></div>
                    <div><Label>State</Label><Input data-testid="c-state" value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} className="mt-1.5" /></div>
                    <div><Label>ZIP code</Label><Input data-testid="c-zip" value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})} className="mt-1.5" /></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#57534E] uppercase tracking-widest mb-3">Emergency contact</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Emergency contact name</Label><Input data-testid="c-emerg-name" value={form.emergency_contact_name} onChange={(e) => setForm({...form, emergency_contact_name: e.target.value})} className="mt-1.5" /></div>
                    <div><Label>Emergency contact phone</Label><Input data-testid="c-emerg-phone" value={form.emergency_contact_phone} onChange={(e) => setForm({...form, emergency_contact_phone: e.target.value})} className="mt-1.5" /></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#57534E] uppercase tracking-widest mb-3">Intake information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Referral source</Label><Input data-testid="c-referral" value={form.referral_source} onChange={(e) => setForm({...form, referral_source: e.target.value})} className="mt-1.5" placeholder="e.g. Dr. Smith, Insurance" /></div>
                    <div><Label>Intake date</Label><Input data-testid="c-intake-date" type="date" value={form.intake_date} onChange={(e) => setForm({...form, intake_date: e.target.value})} className="mt-1.5" /></div>
                  </div>
                  <div className="mt-3"><Label>Insurance information</Label><Textarea data-testid="c-insurance" rows={2} value={form.insurance_info} onChange={(e) => setForm({...form, insurance_info: e.target.value})} className="mt-1.5" placeholder="Provider · Policy # · Group #" /></div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#57534E] uppercase tracking-widest mb-3">Additional</h4>
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                      <SelectTrigger data-testid="c-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-3"><Label>Notes</Label><Textarea data-testid="c-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" /></div>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button data-testid="cancel-client-btn" onClick={() => { setForm(EMPTY); setOpen(false); }} variant="outline" className="rounded-full">Cancel</Button>
                <Button data-testid="save-client-btn" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                  {saving ? "Saving…" : "Save client"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Summary counters — computed from real client data. */}
      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="clients-counters">
        <CounterCard id="total"    label="Total"    value={counters.total}    onClick={() => setStatusFilter("all")}      active={statusFilter === "all"} />
        <CounterCard id="active"   label="Active"   value={counters.active}   onClick={() => setStatusFilter("active")}   active={statusFilter === "active"} />
        <CounterCard id="pending"  label="Pending"  value={counters.pending}  onClick={() => setStatusFilter("pending")}  active={statusFilter === "pending"} />
        <CounterCard id="inactive" label="Inactive" value={counters.inactive} onClick={() => setStatusFilter("inactive")} active={statusFilter === "inactive"} />
        <CounterCard id="archived" label="Archived" value={counters.archived} onClick={() => setStatusFilter("archived")} active={statusFilter === "archived"} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or phone…" className="pl-9 bg-white border-[#E8E2D9]" />
        </div>
        <div className="flex items-center gap-1 bg-white rounded-full border border-[#E8E2D9] p-1">
          {TABS.map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                statusFilter === s ? "bg-[#E8712C] text-white" : "text-[#57534E] hover:text-[#1C1917]"
              }`}>{s}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet"
          description="Add your first client to start building your workspace."
          action={<Button data-testid="empty-add-client" onClick={() => setOpen(true)} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={16} className="mr-1.5" />Add new client</Button>}
        />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#57534E] bg-white rounded-2xl border border-[#E8E2D9]" data-testid="clients-empty-for-filter">
          {statusFilter === "archived" ? "No archived clients." : "No clients match your search."}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          {/* Desktop table (≥md) */}
          <Table className="hidden md:table">
            <TableHeader>
              <TableRow className="border-b border-[#E8E2D9] hover:bg-transparent">
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Name</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Phone</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Email</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Status</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Last activity</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E] w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} onClick={() => navigate(`/app/clients/${c.id}`)} className="cursor-pointer">
                  <TableCell>
                    <div className="font-medium">{c.first_name} {c.last_name}</div>
                    {c.preferred_name && <div className="text-xs text-[#A8A29E]">"{c.preferred_name}"</div>}
                  </TableCell>
                  <TableCell className="text-sm text-[#57534E]">{c.phone || "—"}</TableCell>
                  <TableCell className="text-sm text-[#57534E]">{c.email || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge value={c.status} testid={`status-${c.id}`} />
                      {c.archived && <span data-testid={`archived-badge-${c.id}`} className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700">Archived</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-[#57534E]">{formatDate(c.updated_at || c.created_at)}</TableCell>
                  <TableCell className="text-right"><RowActions c={c} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Mobile card layout (<md) */}
          <div className="md:hidden divide-y divide-[#E8E2D9]">
            {filtered.map((c) => (
              <div key={c.id} className="px-4 py-4 flex items-start gap-3 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors">
                <button
                  onClick={() => navigate(`/app/clients/${c.id}`)}
                  data-testid={`client-card-${c.id}`}
                  className="flex-1 text-left flex items-start gap-3"
                >
                  <div className="w-11 h-11 rounded-full bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] font-bold text-sm flex-shrink-0">
                    {(c.first_name?.[0] || "?").toUpperCase()}{(c.last_name?.[0] || "").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-base truncate">{c.first_name} {c.last_name}</div>
                      <StatusBadge value={c.status} testid={`status-${c.id}`} />
                      {c.archived && <span data-testid={`archived-badge-${c.id}`} className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700">Archived</span>}
                    </div>
                    {c.email && <div className="text-sm text-[#57534E] truncate mt-0.5">{c.email}</div>}
                    {c.phone && <div className="text-sm text-[#57534E] truncate">{c.phone}</div>}
                    <div className="text-xs text-[#A8A29E] mt-1">Last activity · {formatDate(c.updated_at || c.created_at)}</div>
                  </div>
                </button>
                <RowActions c={c} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
